import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import queryOrgStorage from "../queries/orgStorage";

const normalizeBreakdown = (breakdown) => {
  if (!breakdown) return {};

  if (typeof breakdown === "string") {
    try {
      return JSON.parse(breakdown);
    } catch (error) {
      console.warn("Unable to parse org_storage.breakdown", error);
      return {};
    }
  }

  if (typeof breakdown === "object") {
    return breakdown;
  }

  return {};
};

const useOrgStorage = (orgId) => {
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchOrgStorage = useCallback(async () => {
    if (!orgId) {
      setStorage(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: queryOrgStorage,
          variables: {
            orgId,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = response.data;
      const graphqlErrors = responseData?.errors || response?.data?.errors;

      if (graphqlErrors?.length) {
        throw new Error(graphqlErrors[0].message);
      }

      const edges =
        responseData?.org_storages?.edges ||
        responseData?.data?.org_storages?.edges ||
        [];

      const node = edges[0]?.node;

      if (!node) {
        setStorage(null);
        return;
      }

      setStorage({
        ...node,
        total_bytes: Number(node.total_bytes || 0),
        total_files: Number(node.total_files || 0),
        breakdown: normalizeBreakdown(node.breakdown),
      });
    } catch (err) {
      console.error("Errore durante il recupero di org_storage:", err);
      setError(err.message || "Errore nel caricamento di org_storage");
      setStorage(null);
    } finally {
      setLoading(false);
    }
  }, [env, getTokenSilently, orgId]);

  useEffect(() => {
    fetchOrgStorage();
  }, [fetchOrgStorage]);

  return {
    storage,
    loading,
    error,
    refetch: fetchOrgStorage,
  };
};

export default useOrgStorage;
